import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Percent, Info, Landmark, TrendingUp, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatEuro } from "@/data/properties";
import useSEO from "@/hooks/useSEO";

export default function MutuoCheck() {
  useSEO({ title: "Mutuo Check — Simula la Rata del tuo Mutuo | Casamore Group", description: "Calcola la rata mensile del mutuo con tassi indicativi aggiornati alla BCE. Simulazione gratuita e senza impegno con Casamore Group.", canonical: window.location.origin + "/mutuo" });

  const [rates, setRates] = useState(null);
  const [prezzo, setPrezzo] = useState(200000);
  const [anticipo, setAnticipo] = useState(40000);
  const [anni, setAnni] = useState(25);
  const [tipo, setTipo] = useState("fisso");
  const [tasso, setTasso] = useState(3.5);
  const [tassoTouched, setTassoTouched] = useState(false);

  const [form, setForm] = useState({ nome: "", telefono: "", email: "" });
  const [consenso, setConsenso] = useState(false);

  useEffect(() => {
    api.get("/public/mutuo/rates").then((r) => {
      setRates(r.data);
      setTasso(r.data.tassoFisso);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!rates || tassoTouched) return;
    setTasso(tipo === "fisso" ? rates.tassoFisso : rates.tassoVariabile);
  }, [tipo, rates, tassoTouched]);

  const importo = Math.max(0, prezzo - anticipo);
  const ltv = prezzo > 0 ? Math.round((importo / prezzo) * 100) : 0;

  const { rata, totale, interessi } = useMemo(() => {
    const r = tasso / 100 / 12;
    const n = anni * 12;
    const rata = r === 0 ? importo / n : (importo * r) / (1 - Math.pow(1 + r, -n));
    const totale = rata * n;
    return { rata, totale, interessi: totale - importo };
  }, [importo, tasso, anni]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono) { toast.error("Inserisci nome e telefono."); return; }
    if (!consenso) { toast.error("Accetta l'informativa privacy."); return; }
    try {
      await api.post("/public/leads", {
        tipo: "mutuo",
        contatto: { nome: form.nome, telefono: form.telefono, email: form.email || null },
        payload: {
          "Prezzo immobile": formatEuro(prezzo), "Anticipo": formatEuro(anticipo),
          "Importo mutuo": formatEuro(importo), "Durata": `${anni} anni`,
          "Tipo tasso": tipo, "Tasso": `${tasso.toFixed(2)}%`, "Rata stimata": formatEuro(rata),
        },
        consensoPrivacy: true,
        provenienza: "Mutuo Check",
      });
      toast.success("Richiesta inviata! Un consulente ti ricontatterà.");
      setForm({ nome: "", telefono: "", email: "" }); setConsenso(false);
    } catch (err) {
      if (err?.response?.status === 429) toast.error("Troppe richieste, riprova tra poco.");
      else toast.error("Invio non riuscito, riprova.");
    }
  };

  const fld = "bg-white border-border-subtle focus-visible:ring-brand/30 focus-visible:border-brand rounded-xl h-12";

  return (
    <div className="pt-28 pb-24">
      <div className="mx-auto max-w-[1100px] px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-brand">
            <Landmark className="h-4 w-4" /> Mutuo Check
          </span>
          <h1 className="font-display font-semibold text-anthracite text-5xl sm:text-6xl tracking-tight mt-4 leading-[0.95]">
            Simula la rata del <span className="italic text-brand">tuo mutuo</span>
          </h1>
          <p className="mt-4 text-lg text-anthracite/70 max-w-xl">
            Inserisci i tuoi dati e scopri una stima della rata mensile. Tassi indicativi aggiornati, senza alcun impegno.
          </p>
        </motion.div>

        {/* Live rates banner */}
        {rates && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="mutuo-rates">
            <div className="rounded-2xl border border-border-subtle bg-white p-4 flex items-center gap-3">
              <Percent className="h-5 w-5 text-brand" />
              <div><p className="text-xs text-anthracite/50">Rif. BCE</p><p className="font-display text-xl font-semibold text-anthracite">{rates.riferimentoBce}%</p></div>
            </div>
            <div className="rounded-2xl border border-border-subtle bg-white p-4 flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-brand" />
              <div><p className="text-xs text-anthracite/50">Tasso fisso indic.</p><p className="font-display text-xl font-semibold text-anthracite">{rates.tassoFisso}%</p></div>
            </div>
            <div className="rounded-2xl border border-border-subtle bg-white p-4 flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-brand" />
              <div><p className="text-xs text-anthracite/50">Tasso variabile indic.</p><p className="font-display text-xl font-semibold text-anthracite">{rates.tassoVariabile}%</p></div>
            </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Simulator */}
          <div className="lg:col-span-3 rounded-3xl border border-border-subtle bg-white p-6 sm:p-8" data-testid="mutuo-simulator">
            <div className="flex gap-2 mb-6">
              {["fisso", "variabile"].map((t) => (
                <button key={t} onClick={() => { setTipo(t); setTassoTouched(false); }} className={`flex-1 rounded-xl py-3 text-sm font-semibold capitalize transition-all ${tipo === t ? "bg-brand text-white" : "bg-accent-subtle text-anthracite/70"}`} data-testid={`mutuo-tipo-${t}`}>
                  Tasso {t}
                </button>
              ))}
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex justify-between text-sm mb-2"><span className="text-anthracite/60">Prezzo immobile</span><span className="font-semibold text-brand">{formatEuro(prezzo)}</span></div>
                <Slider value={[prezzo]} min={50000} max={800000} step={5000} onValueChange={([v]) => setPrezzo(v)} data-testid="mutuo-prezzo" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2"><span className="text-anthracite/60">Anticipo</span><span className="font-semibold text-brand">{formatEuro(anticipo)}</span></div>
                <Slider value={[anticipo]} min={0} max={prezzo} step={5000} onValueChange={([v]) => setAnticipo(v)} data-testid="mutuo-anticipo" />
                <p className={`mt-2 text-xs ${ltv > 80 ? "text-red-500" : "text-anthracite/45"}`}>Importo finanziato: {formatEuro(importo)} — LTV {ltv}%{ltv > 80 ? " (oltre l'80%, spesso servono garanzie aggiuntive)" : ""}</p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex justify-between text-sm mb-2"><span className="text-anthracite/60">Durata</span><span className="font-semibold text-brand">{anni} anni</span></div>
                  <Slider value={[anni]} min={5} max={30} step={1} onValueChange={([v]) => setAnni(v)} data-testid="mutuo-anni" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2"><span className="text-anthracite/60">Tasso (TAN)</span><span className="font-semibold text-brand">{tasso.toFixed(2)}%</span></div>
                  <Slider value={[tasso]} min={0.5} max={7} step={0.05} onValueChange={([v]) => { setTasso(v); setTassoTouched(true); }} data-testid="mutuo-tasso" />
                </div>
              </div>
            </div>

            <div className="mt-7 rounded-2xl bg-brand text-white p-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/80">Rata mensile stimata</span>
                <span className="font-display text-4xl font-semibold" data-testid="mutuo-rata">{formatEuro(rata)}</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-white/70">Interessi totali</p><p className="font-semibold">{formatEuro(interessi)}</p></div>
                <div><p className="text-white/70">Totale da rimborsare</p><p className="font-semibold">{formatEuro(totale)}</p></div>
              </div>
            </div>

            <p className="mt-4 flex items-start gap-2 text-xs text-anthracite/45" data-testid="mutuo-disclaimer">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              Stima puramente indicativa a fini illustrativi: non costituisce offerta, consulenza né promessa di finanziamento. Il calcolo non include spese di istruttoria, perizia, imposte e assicurazioni. {rates ? `Tasso di riferimento: ${rates.fonte}${rates.aggiornatoAl ? ` (aggiornato al ${rates.aggiornatoAl})` : ""}.` : ""}
            </p>
          </div>

          {/* Lead form */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24 rounded-3xl border border-border-subtle bg-accent-subtle p-6 sm:p-7">
              <h2 className="font-display text-2xl font-semibold text-anthracite">Parla con un consulente</h2>
              <p className="text-sm text-anthracite/60 mt-1">Ti aiutiamo a trovare la soluzione di mutuo più adatta a te.</p>
              <form onSubmit={submit} className="space-y-3 mt-4" data-testid="mutuo-lead-form">
                <Input className={fld} placeholder="Nome e cognome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="mutuo-nome" />
                <Input className={fld} placeholder="Telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="mutuo-telefono" />
                <Input className={fld} type="email" placeholder="Email (facoltativa)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="mutuo-email" />
                <label className="flex items-start gap-2 text-xs text-anthracite/60">
                  <input type="checkbox" checked={consenso} onChange={(e) => setConsenso(e.target.checked)} className="mt-0.5 accent-[#0F5C4A]" data-testid="mutuo-privacy" />
                  Acconsento al trattamento dei dati secondo l'informativa privacy.
                </label>
                <button type="submit" className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold py-4 hover:bg-brand-hover transition-colors" data-testid="mutuo-submit">
                  Richiedi consulenza <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
